import { useRef, useState } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@monksflow/monks-ui';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import { useTranslation } from 'react-i18next';
import { categoricalBadgeClass, type CategoricalBadgeColor } from '@/shared/ui/categoricalBadge';

interface WorkspaceChipsProps {
  workspaces: WorkspaceResponseDto[];
  /**
   * Palette slot for the chips. It names the workspace-access MODE, not a
   * success/error state, so it maps onto the categorical scale rather than a
   * semantic badge variant.
   */
  color: CategoricalBadgeColor;
  label?: string;
}

function WorkspaceAvatar({ workspace }: { workspace: WorkspaceResponseDto }) {
  return (
    <Avatar className="size-5 shrink-0">
      {workspace.logoUrl && <AvatarImage src={workspace.logoUrl} alt={workspace.name} />}
      <AvatarFallback className="text-[10px]">
        {workspace.name.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

/** Renders a workspace avatar + name in a horizontal row. */
export function WorkspaceItem(props: { workspace: WorkspaceResponseDto }) {
  return (
    <span className="flex flex-row items-center gap-2.5">
      <WorkspaceAvatar workspace={props.workspace} />
      <span className="text-sm leading-5">{props.workspace.name}</span>
    </span>
  );
}

export function WorkspaceChips(props: WorkspaceChipsProps) {
  const { t } = useTranslation('admin');
  const { workspaces, color, label } = props;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  if (workspaces.length === 0) {
    return null;
  }

  const firstWorkspace = workspaces[0];
  const remainingWorkspaces = workspaces.slice(1);

  return (
    // Spans rather than divs: a chip group renders inside the agent-card header
    // button, and a <div> may not descend from a <button>.
    <span className="flex flex-col gap-1">
      {label && <span className="text-xs leading-4 text-muted-foreground">{label}</span>}
      <span className="flex items-center gap-1">
        <Badge roundness="full" className={`gap-1.5 ${categoricalBadgeClass(color)}`}>
          <WorkspaceAvatar workspace={firstWorkspace} />
          {firstWorkspace.name}
        </Badge>

        {remainingWorkspaces.length > 0 && (
          <Popover open={popoverOpen}>
            <PopoverAnchor asChild>
              <span
                ref={anchorRef}
                className="cursor-default"
                onMouseEnter={() => setPopoverOpen(true)}
                onMouseLeave={() => setPopoverOpen(false)}
              >
                <Badge roundness="full" className={categoricalBadgeClass(color)}>
                  {`+${remainingWorkspaces.length}`}
                </Badge>
              </span>
            </PopoverAnchor>

            <PopoverContent
              align="start"
              side="bottom"
              className="max-h-60 w-auto overflow-y-auto p-2"
              // The popover is driven by hover, so pulling focus into it would
              // move the caret away from whatever the user was actually using.
              onOpenAutoFocus={(event) => event.preventDefault()}
              onMouseEnter={() => setPopoverOpen(true)}
              onMouseLeave={() => setPopoverOpen(false)}
            >
              <div className="flex flex-col gap-1">
                <span className="p-2 text-xs leading-4 text-muted-foreground">
                  {t('admin.workspaceChips.otherWorkspaces', {
                    remainingCount: remainingWorkspaces.length,
                  })}
                </span>

                {remainingWorkspaces.map((ws) => (
                  <div key={ws.id} className="p-2">
                    <WorkspaceItem workspace={ws} />
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </span>
    </span>
  );
}
